import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import Layout from './components/layout/Layout';
import DashboardPage from './pages/DashboardPage';
import MonitoringPage from './pages/MonitoringPage';
import CostReportsPage from './pages/CostReportsPage';
import OptimizationPage from './pages/OptimizationPage';
import DatabaseOptimizationPage from './pages/DatabaseOptimizationPage';
import AiQueryPage from './pages/AiQueryPage';
import AlertsPage from './pages/AlertsPage';
import SettingsPage from './pages/SettingsPage';
import SustainabilityPage from './pages/SustainabilityPage';
import LoginPage from './pages/LoginPage';
import CloudConnectionsPage from './pages/CloudConnectionsPage';
import NotificationsSettingsPage from './pages/NotificationsSettingsPage';
import AuditLogPage from './pages/AuditLogPage';
import { useStore } from './store';
import { useRole } from './hooks/useRole';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({ children }) {
  const { token } = useStore();
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function StaffOnly({ children }) {
  const { isStaffOrAbove } = useRole();
  if (!isStaffOrAbove) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: {
              background: '#1a2540',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10,
              fontSize: 14,
              fontFamily: '"DM Sans", sans-serif',
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="monitoring" element={<StaffOnly><MonitoringPage /></StaffOnly>} />
            <Route path="cost" element={<CostReportsPage />} />
            <Route path="optimization" element={<OptimizationPage />} />
            <Route path="db-optimization" element={<StaffOnly><DatabaseOptimizationPage /></StaffOnly>} />
            <Route path="ai" element={<StaffOnly><AiQueryPage /></StaffOnly>} />
            <Route path="alerts" element={<StaffOnly><AlertsPage /></StaffOnly>} />
            <Route path="sustainability" element={<SustainabilityPage />} />
            <Route path="settings" element={<StaffOnly><SettingsPage /></StaffOnly>} />
            <Route path="cloud-connections" element={<StaffOnly><CloudConnectionsPage /></StaffOnly>} />
            <Route path="notifications-settings" element={<NotificationsSettingsPage />} />
            <Route path="audit" element={<StaffOnly><AuditLogPage /></StaffOnly>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
